/**
 * Email Normalization Utility
 * 
 * This utility provides consistent email-to-document-ID conversion
 * that matches the Firestore security rules exactly.
 * 
 * CRITICAL: This normalization MUST match the getEmailDocId function
 * in firestore.rules (lines 6-37) exactly, or permission errors will occur.
 */

/**
 * Normalizes an email address to a Firestore-safe document ID.
 * 
 * Rules:
 * 1. Convert to lowercase (emails are case-insensitive)
 * 2. Replace special characters with underscores
 * 3. Characters replaced: .@#$%^&*()+=!?<>{}[]|\:;"'`~/ (space too)
 * 
 * @param {string} email - The email address to normalize
 * @returns {string} - The normalized document ID
 * 
 * @example
 * normalizeEmailToDocId('John.Doe@Example.com') 
 * // returns 'john_doe_example_com'
 */
export function normalizeEmailToDocId(email) {
  if (!email || typeof email !== 'string') {
    console.warn('Invalid email provided to normalizeEmailToDocId:', email);
    return '';
  }
    
  // Step 1: Convert to lowercase for case-insensitive matching
  const lowerEmail = email.toLowerCase().trim();
    
  // Step 2: Replace special characters with underscores
  // This matches the Firestore rules getEmailDocId function
  const normalized = lowerEmail.replace(/[.@#$%^&*()+=!?<>{}[\]|\\:;"'`~/ ]/g, '_');
    
  return normalized;
}

/**
 * Validates if an email address is properly formatted.
 * 
 * SECURITY: Enhanced validation to prevent:
 * - Homograph attacks (non-ASCII lookalike characters)
 * - Email injection attacks
 * - Invalid email formats
 * 
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidEmail(email) {
  if (!email || typeof email !== 'string') return false;
    
  const trimmed = email.trim();
    
  // Length check (RFC 5321: max 320 chars)
  if (trimmed.length > 320) return false;
    
  // ASCII only (prevent homograph attacks)
  // eslint-disable-next-line no-control-regex
  if (!/^[\x00-\x7F]*$/.test(trimmed)) return false;
    
  // Strict email format validation
  const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  return emailRegex.test(trimmed);
}

/**
 * Normalizes and validates an email address with enhanced safety checks.
 * Use this function in critical invitation flows for extra protection.
 * 
 * @param {string} email - The email address to normalize and validate
 * @returns {string} - The normalized lowercase email
 * @throws {Error} - If email is invalid or normalization fails
 * 
 * @example
 * try {
 *   const email = normalizeAndValidateEmail('Test@Example.com');
 *   // email = 'test@example.com'
 * } catch (error) {
 *   console.error('Invalid email:', error.message);
 * }
 */
export function normalizeAndValidateEmail(email) {
  if (!email || typeof email !== 'string') {
    throw new Error('Invalid email: must be a non-empty string');
  }
    
  const trimmed = email.trim();
    
  if (!isValidEmail(trimmed)) {
    throw new Error('Invalid email format');
  }
    
  // Force lowercase
  const normalized = trimmed.toLowerCase();
    
  // Verify no unusual characters remain after normalization
  if (/[A-Z]/.test(normalized)) {
    throw new Error('Email normalization failed - contains uppercase letters');
  }
    
  return normalized;
}

/**
 * Gets a user's document path in the team container.
 * 
 * @param {string} appId - The Firebase project ID
 * @param {string} teamId - The team ID
 * @param {string} email - The user's email address
 * @returns {string} - The full Firestore document path
 */
export function getUserDocPath(appId, teamId, email) {
  const emailDocId = normalizeEmailToDocId(email);
  return `artifacts/${appId}/teams/${teamId}/users/${emailDocId}`;
}

/**
 * Extracts the email document ID from a user's email.
 * This is a convenience wrapper around normalizeEmailToDocId.
 * 
 * @param {string} email - The email address
 * @returns {string} - The normalized document ID
 */
export function getEmailDocId(email) {
  return normalizeEmailToDocId(email);
}







